// @noflow

import { isEmpty } from 'lodash';
import { PlayerEvent } from '../../../player/utils';
import PlayerManager from '../PlayerManager';
import FreeWheelAd from '../FreeWheelAd';

export const getFreeWheelAd = (
  customAttributes,
  qvtPlaybackInfo,
  duration,
  visitorIp,
  os,
  deviceId,
  uuid,
  shouldReset,
  limitAdTracking,
  googleAdvertisingId,
) => {
  const {
    mrm_site_section_id_live,
    freewheel_network_id,
    freewheel_profile_id,
    freewheel_server_url,
  } = customAttributes;
  const { asset } = qvtPlaybackInfo;

  const freewheelAd = Object.assign(new FreeWheelAd(os === 'web' ? 'vmap1' : 'vast4'), {
    adServerUrl: freewheel_server_url,
    networkId: freewheel_network_id,
    profile: freewheel_profile_id,
    siteSection: mrm_site_section_id_live,
    videoAssetId: asset.publisher_asset_id,
    clipDurationSeconds: duration,
  });

  freewheelAd.mode = 'live';
  freewheelAd.reset = shouldReset;
  freewheelAd.visitorIp = visitorIp;
  if (deviceId) {
    if (os === 'ios') {
      freewheelAd.deviceId = `idfa:${deviceId}`;
    } else {
      if (googleAdvertisingId) {
        freewheelAd.deviceId = `google_advertising_id:${googleAdvertisingId}`;
      }
    }
  }
  freewheelAd.uuid = uuid;
  freewheelAd.limitAdTracking = limitAdTracking;

  return freewheelAd;
};

export const getLLAdSlots = (
  customAttributes,
  qvtPlaybackInfo,
  adaptUrl,
  visitorIp,
  os,
  deviceId,
  uuid,
  shouldReset,
  limitAdTracking,
  googleAdvertisingId,
) => {
  const {
    ad_info: { ad_breaks: adBreaks },
    linear_info: { anchor_time: anchorTime },
  } = qvtPlaybackInfo;

  return adBreaks.map((adBreak, index) => {
    const freewheelAd = getFreeWheelAd(
      customAttributes,
      qvtPlaybackInfo,
      1800,
      visitorIp,
      os,
      deviceId,
      uuid,
      shouldReset && index === 0,
      limitAdTracking,
      googleAdvertisingId,
    );

    const duration = adBreak.stop_offset - adBreak.start_offset;

    freewheelAd.adSlots = [
      {
        slotId: `Slot_${index}`,
        slotPosition: 'MIDROLL',
        timePosition: adBreak.start_offset,
        minDuration: duration,
        maxDuration: duration,
      },
    ];

    return {
      adUrl: `${adaptUrl}${freewheelAd.url}`,
      adType: 'MIDROLL',
      timePosition: adBreak.start_offset - anchorTime,
      duration,
    };
  });
};

class LiveLinearAdManager extends PlayerManager {
  constructor(playerDelegate, adaptUrl, analyticsId, userIp) {
    super(playerDelegate);
    this._adaptUrl = adaptUrl;
    this.numberOfAds = 0;
    this.analyticsId = analyticsId;
    this.userIp = userIp;
    this.videoAssetId = undefined;
  }

  get enabled() {
    return !!this._videoMetadata.custom_attributes.freewheel_enabled;
  }

  get events() {
    return {
      [PlayerEvent.SourceLoaded]: () => {
        this.adSlotInterval = setInterval(
          () => this.getAdSlots(this.analyticsId, this.userIp),
          1000,
        );
      },
      [PlayerEvent.SourceUnloaded]: () => {
        clearInterval(this.adSlotInterval);
      },
      [PlayerEvent.PlaybackFinished]: () => {
        clearInterval(this.adSlotInterval);
      },
      [PlayerEvent.Destroy]: () => {
        clearInterval(this.adSlotInterval);
      },
    };
  }

  getAdSlots = (analyticsId, userIp) => {
    const { qvt: qvtPayload, airing } = this._airing;
    const { videoMetadata, playerType, limitAdTracking } = this._delegate._player.props;
    const { playback_info: playbackInfo } = qvtPayload;
    // const { asset: { publisher_asset_id: newVideoAssetId } = {}} = qvtPayload;
    const { duration } = airing;
    const { ad_info: adInfo, linear_info: linearInfo, asset } = playbackInfo;
    const { publisher_asset_id: newVideoAssetId } = asset;
    const shouldReset = this.videoAssetId !== newVideoAssetId;
    this.videoAssetId = newVideoAssetId;

    if (!adInfo) {
      return;
    }

    if (playerType !== 'nosling') {
      return;
    }

    const { ad_breaks: adBreaks } = adInfo;
    if (!isEmpty(adBreaks) && adBreaks.length !== this.numberOfAds) {
      this.numberOfAds = adBreaks.length;

      const nextAdBreak = adBreaks[adBreaks.length - 1];
      const adjustedAdStart = nextAdBreak.start_offset + linearInfo.playback_delay;
      const replaceContentDuration = nextAdBreak.stop_offset - nextAdBreak.start_offset;

      if (adjustedAdStart < this._delegate.currentTime) {
        return;
      }

      const freewheelAd = getFreeWheelAd(
        videoMetadata.custom_attributes,
        playbackInfo,
        duration,
        userIp,
        'web',
        null,
        analyticsId,
        shouldReset,
        limitAdTracking,
      );

      freewheelAd.adSlots = adBreaks.map((slot, index) => ({
        slotId: `Slot_${index}`,
        slotPosition: slot.type.toLowerCase(),
        timePosition: slot.start_offset,
      }));

      const adConfig = {
        tag: {
          url: `${this._adaptUrl}${freewheelAd.url}`,
          type: 'vmap',
        },
        replaceContentDuration: Math.round(replaceContentDuration),
        position: String(adjustedAdStart),
        id: `Slot_${adBreaks.length}`,
      };

      this._delegate._player.playerView.ads.schedule(adConfig);
    }
  };
}

export default LiveLinearAdManager;
